import { Collapse, RotatingChevron } from "@newsela/angelou";
import { isEmpty } from "@s-libs/micro-dash";
import { useMemo } from "react";

import AILunaTag from "@/components/AIComponents/AILunaTag/AILunaTag";
import { IStandard } from "@/search/Langston/types/IStandard";
import ContentLevel from "@/types/models/ContentLevel.interface";
import { User } from "@/types/models/User/v3";
import {
  LessonPlanRequestInterface,
  useGetLessonPlan,
} from "@/view/ContentDisplay/TeacherTools/useGetLessonPlan/useGetLessonPlan";
import {
  collapseContentWrapperCss,
  collapseCss,
  lessonPlanFooterCss,
  lessonPlanWrapperCss,
} from "./LessonPlan.style";
import LessonPlanContent from "./LessonPlanContent";
import LessonPlanFooterActions from "./LessonPlanFooterActions";
import LessonPlannerErrorState from "./LessonPlannerErrorState";
import LessonPlannerLoadingState from "./LessonPlannerLoadingState";
import { formatLessonPlan, useLessonPlanEditor } from "./utils";
import {
  removeStoredLesson,
  updateStoredLesson,
} from "./utils/lessonPlanStorage";
import { ILessonPlanNode } from "./utils/useLessonPlanEditor/useLessonPlanEditor";

interface LessonPlanProps {
  contentId: string;
  currentLevel: ContentLevel;
  setLessonPlanFormIsComplete: (show: boolean) => void;
  lessonPlanRequest: LessonPlanRequestInterface;
  reset: () => void;
  extraData?:
    | {
        standards?: IStandard[];
        objective?: string;
      }
    | undefined;
  savedLesson: ILessonPlanNode | null;
  user: User | null | undefined;
}

const LessonPlan = ({
  contentId,
  currentLevel,
  setLessonPlanFormIsComplete,
  lessonPlanRequest,
  reset,
  extraData,
  savedLesson,
  user,
}: LessonPlanProps) => {
  const isSavedLessonEmpty = isEmpty(savedLesson);
  const { data, error, loading } = useGetLessonPlan({
    ...lessonPlanRequest,
    skip: !isSavedLessonEmpty,
  });
  const lessonPlan = savedLesson || data?.prosemirror_document;
  const formattedContent = useMemo(
    () => formatLessonPlan({ lessonPlan }),
    [lessonPlan],
  );

  if (isSavedLessonEmpty && !isEmpty(lessonPlan)) {
    updateStoredLesson({
      user,
      contentId,
      lessonPlan,
    });
  }

  const editor = useLessonPlanEditor({ content: formattedContent });

  const handleReset = () => {
    reset();
    removeStoredLesson({ user, contentId });
    setLessonPlanFormIsComplete(false);
  };

  if (loading) {
    return <LessonPlannerLoadingState onCancel={handleReset} />;
  }

  // Show error state if lesson plan is somehow empty (i.e. prosemirror_doc is null)
  if (error || isEmpty(lessonPlan)) {
    return <LessonPlannerErrorState onTryAgain={handleReset} />;
  }

  return (
    <>
      <div css={lessonPlanWrapperCss}>
        <AILunaTag textVersion="long" />
        <Collapse
          __cssFor={collapseCss}
          renderButtonContents={(isHidden) => (
            <div css={collapseContentWrapperCss}>
              <h3>Your lesson plan</h3>
              <RotatingChevron isDown={isHidden} />
            </div>
          )}
          initialIsHidden={false}
        >
          <LessonPlanContent
            editor={editor}
            contentId={contentId}
            currentLevel={currentLevel}
            user={user}
          />
        </Collapse>
      </div>
      <div css={lessonPlanFooterCss}>
        <LessonPlanFooterActions
          contentId={contentId}
          handleReset={handleReset}
          extraData={extraData}
        />
      </div>
    </>
  );
};

export default LessonPlan;
